import { useEffect, useMemo, useState } from 'react';
import useExchangeBalance from './use-exchange-balance.js';
import useFromAsset from './use-from-asset.js';
import useFromWalletAccount from './use-from-wallet-account.js';
import useIsBalanceExchangeable from './use-is-balance-exchangeable.js';
import { useLimitsContext } from '../context/limits.js';
import { useSelector } from 'react-redux';
import { useExodus, useSelectors } from '@exodus/headless-react';
import useToAsset from './use-to-asset.js';
import { getQuoteId } from './use-quote.js';
import useToWalletAccount from './use-to-wallet-account.js';
import useSlippage from './use-slippage.js';
import { getExchangeFee } from './use-exchange-fee.js';
const useExchangePrefills = () => {
    const [fromWalletAccount] = useFromWalletAccount();
    const { value: limits } = useLimitsContext();
    const selectors = useSelectors();
    const [loadingFees, setLoadingFees] = useState(true);
    const [fromAsset] = useFromAsset();
    const [toAsset] = useToAsset();
    const isBalanceExchangeable = useIsBalanceExchangeable();
    const [toWalletAccount] = useToWalletAccount();
    const [slippage] = useSlippage();
    const exodus = useExodus();
    const quotes = useSelector(selectors.exchange.quotes);
    const balance = useExchangeBalance({
        assetName: fromAsset.name,
        walletAccount: fromWalletAccount.toString(),
    });
    const balanceQuoteId = useMemo(() => {
        if (!isBalanceExchangeable) {
            return null;
        }
        return getQuoteId({
            fromAssetName: fromAsset.name,
            toAssetName: toAsset.name,
            amount: balance,
        });
    }, [isBalanceExchangeable, fromAsset.name, toAsset.name, balance]);
    useEffect(() => {
        if (balanceQuoteId) {
            void exodus.exchange.getBestQuote({
                amount: balance,
                fromAssetName: fromAsset.name,
                toAssetName: toAsset.name,
                slippage,
                quoteId: balanceQuoteId,
                fromWalletAccount: fromWalletAccount.toString(),
                toWalletAccount: toWalletAccount.toString(),
                isSendAll: true,
            });
        }
    }, [balanceQuoteId]);
    const isFromFeeAsset = fromAsset.feeAsset.name === fromAsset.name;
    useEffect(() => {
        setLoadingFees(isFromFeeAsset && !!balanceQuoteId);
    }, [isFromFeeAsset, fromAsset, balanceQuoteId]);
    const fullBalanceQuoteFee = useMemo(() => {
        if (!balanceQuoteId)
            return null;
        const quote = !quotes[balanceQuoteId]?.loading && quotes[balanceQuoteId]?.quote;
        if (quote && isFromFeeAsset) {
            const fees = getExchangeFee({ fromAsset, quote });
            setLoadingFees(false);
            return fees;
        }
        return null;
    }, [balanceQuoteId, fromAsset, isFromFeeAsset, quotes]);
    const balanceMinusFee = useMemo(() => fullBalanceQuoteFee?.isPositive ? balance.sub(fullBalanceQuoteFee).clampLowerZero() : balance, [balance, fullBalanceQuoteFee]);
    const balanceMinusFeeQuoteId = useMemo(() => getQuoteId({
        fromAssetName: fromAsset.name,
        toAssetName: toAsset.name,
        amount: balanceMinusFee,
    }), [balanceMinusFee, fromAsset.name, toAsset.name]);
    useEffect(() => {
        if (balanceMinusFeeQuoteId && fullBalanceQuoteFee?.isPositive) {
            void exodus.exchange.getBestQuote({
                amount: balanceMinusFee,
                fromAssetName: fromAsset.name,
                toAssetName: toAsset.name,
                slippage,
                quoteId: balanceMinusFeeQuoteId,
                fromWalletAccount: fromWalletAccount.toString(),
                toWalletAccount: toWalletAccount.toString(),
                isSendAll: true,
            });
        }
    }, [balanceMinusFeeQuoteId]);
    const loading = useMemo(() => {
        if (!balance || !limits?.ExactIn || loadingFees) {
            return true;
        }
        return (balanceQuoteId &&
            (!quotes[balanceQuoteId] || (isFromFeeAsset && fullBalanceQuoteFee?.isPositive)
                ? quotes[balanceQuoteId]?.loading
                : false));
    }, [balance, balanceQuoteId, fullBalanceQuoteFee, isFromFeeAsset, limits, quotes, loadingFees]);
    const isBalanceMinusFeeExchangeable = limits?.ExactIn
        ? balanceMinusFee.gte(limits.ExactIn.min) && balanceMinusFee.lte(limits.ExactIn.max)
        : false;
    const prefillAmounts = useMemo(() => {
        if (loading || !limits) {
            const zero = fromAsset.currency.ZERO;
            return {
                min: { label: 'MIN', value: zero },
                half: { label: 'HALF', value: zero },
                max: { label: 'MAX', value: zero },
            };
        }
        const max = isBalanceMinusFeeExchangeable ? balanceMinusFee : limits.ExactIn.max;
        return {
            min: { label: 'MIN', value: limits.ExactIn.min },
            half: { label: 'HALF', value: max.div(2) },
            max: { label: 'MAX', value: max },
        };
    }, [balanceMinusFee, fromAsset.currency.ZERO, isBalanceMinusFeeExchangeable, limits, loading]);
    return {
        loading,
        prefillAmounts,
        isPrefillExchangeable: isBalanceMinusFeeExchangeable,
    };
};
export default useExchangePrefills;
